import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Save, Mail, Linkedin, Instagram, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminContact = () => {
  const { getAuthHeaders } = useAdmin();
  const [formData, setFormData] = useState({
    email: '',
    email_display: '',
    linkedin_url: '',
    linkedin_display: '',
    instagram_url: '',
    instagram_display: '',
    register_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContact();
  }, []);

  const fetchContact = async () => {
    try {
      const response = await axios.get(`${API}/admin/contact`, getAuthHeaders());
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching contact:', error);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/contact`, formData, getAuthHeaders());
      alert('Contact information updated successfully!');
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Error saving changes: ' + (error.response?.data?.detail || error.message));
    }
    setSaving(false);
  };

  if (loading) return <AdminLayout><p className="text-white">Loading...</p></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <h1 className="display-medium text-white">Contact Information</h1>
        <p className="body-medium text-white/60">Manage the contact details shown on the "Get In Touch" page</p>

        <div className="bg-white/10 border border-white/25 p-6 rounded-lg">
          <div className="space-y-6">
            {/* Email Section */}
            <div className="border-b border-white/10 pb-6">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="text-[#00FFD1]" size={20} />
                <h2 className="heading-3 text-white">Email</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="body-small text-white mb-1 block">Email Address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                    placeholder="hello@deftexperiences.com"
                    data-testid="contact-email-input"
                  />
                  <p className="text-white/40 text-xs mt-1">Used for the mailto: link</p>
                </div>
                <div>
                  <label className="body-small text-white mb-1 block">Display Text</label>
                  <input
                    type="text"
                    value={formData.email_display}
                    onChange={(e) => setFormData({ ...formData, email_display: e.target.value })}
                    className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                    placeholder="hello@deftexperiences.com"
                    data-testid="contact-email-display-input"
                  />
                  <p className="text-white/40 text-xs mt-1">Text shown on the page</p>
                </div>
              </div>
            </div>

            {/* LinkedIn Section */}
            <div className="border-b border-white/10 pb-6">
              <div className="flex items-center gap-2 mb-4">
                <Linkedin className="text-[#00FFD1]" size={20} />
                <h2 className="heading-3 text-white">LinkedIn</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="body-small text-white mb-1 block">LinkedIn URL</label>
                  <input
                    type="url"
                    value={formData.linkedin_url}
                    onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                    className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                    placeholder="https://www.linkedin.com/company/deft-experiences/"
                    data-testid="contact-linkedin-url-input"
                  />
                </div>
                <div>
                  <label className="body-small text-white mb-1 block">Display Name</label>
                  <input
                    type="text"
                    value={formData.linkedin_display}
                    onChange={(e) => setFormData({ ...formData, linkedin_display: e.target.value })}
                    className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                    placeholder="Deft Experiences"
                    data-testid="contact-linkedin-display-input"
                  />
                </div>
              </div>
            </div>

            {/* Instagram Section */}
            <div className="border-b border-white/10 pb-6">
              <div className="flex items-center gap-2 mb-4">
                <Instagram className="text-[#00FFD1]" size={20} />
                <h2 className="heading-3 text-white">Instagram</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="body-small text-white mb-1 block">Instagram URL</label>
                  <input
                    type="url"
                    value={formData.instagram_url}
                    onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                    className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                    placeholder="https://www.instagram.com/deft_experiences"
                    data-testid="contact-instagram-url-input"
                  />
                </div>
                <div>
                  <label className="body-small text-white mb-1 block">Display Handle</label>
                  <input
                    type="text"
                    value={formData.instagram_display}
                    onChange={(e) => setFormData({ ...formData, instagram_display: e.target.value })}
                    className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                    placeholder="@deft_experiences"
                    data-testid="contact-instagram-display-input"
                  />
                </div>
              </div>
            </div>

            {/* Registration Link Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <ExternalLink className="text-[#00FFD1]" size={20} />
                <h2 className="heading-3 text-white">Registration Link</h2>
              </div>
              <div>
                <label className="body-small text-white mb-1 block">Register Interest URL</label>
                <input
                  type="url"
                  value={formData.register_url}
                  onChange={(e) => setFormData({ ...formData, register_url: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                  placeholder="https://deftexp.fillout.com/lessgo"
                  data-testid="contact-register-url-input"
                />
                <p className="text-white/40 text-xs mt-1">Link for the "Register Your Interest" button</p>
              </div>
            </div>

            <button 
              onClick={handleSave} 
              disabled={saving} 
              className="btn-primary flex items-center gap-2 mt-6"
              data-testid="save-contact-btn"
            >
              <Save size={20} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminContact;
